/**
 * Default sensitive field patterns that should be redacted from logs
 * These patterns are case-insensitive and match common sensitive data fields
 */
const DEFAULT_SENSITIVE_PATTERNS = [
  // Password variations
  /^password$/i,
  /^pwd$/i,
  /^passwd$/i,
  /password/i,
  
  // Specific token field names
  /^token$/i,
  /^accesstoken$/i,
  /^access_token$/i,
  /^refreshtoken$/i,
  /^refresh_token$/i,
  /^apitoken$/i,
  /^api_token$/i,
  /^usertoken$/i,
  /^user_token$/i,
  /^idtoken$/i,
  /^id_token$/i,
  /^jwttoken$/i,
  /^jwt_token$/i,
  /^jwt$/i,
  
  // Secret variations
  /^secret$/i,
  /^apisecret$/i,
  /^api_secret$/i,
  /^clientsecret$/i,
  /^client_secret$/i,
  /secret$/i,
  
  // API Key variations
  /^apikey$/i,
  /^api_key$/i,
  /^key$/i,
  /apikey/i,
  
  // Connection strings
  /^connectionstring$/i,
  /^connection_string$/i,
  /^connstr$/i,
  /^conn_str$/i,
  /connectionstring/i,
  
  // Authorization
  /^authorization$/i,
  /^auth$/i,
  /authorization/i,
  
  // Personal Identification Numbers
  /^ssn$/i,
  /^socialsecurity$/i,
  /^social_security$/i,
  /^socialsecuritynumber$/i,
  /^social$/i,
  
  // Driver's License
  /^driverslicense$/i,
  /^drivers_license$/i,
  /^driverlicense$/i,
  /^driver_license$/i,
  /^dl$/i,
  /^dlnumber$/i,
  /^dl_number$/i,
  
  // Passport Information
  /^passport$/i,
  /^passportnumber$/i,
  /^passport_number$/i,
  /^passportid$/i,
  /^passport_id$/i,
  
  // Tax Identification
  /^taxid$/i,
  /^tax_id$/i,
  /^ein$/i,
  /^itin$/i,
  /^taxpayerid$/i,
  /^taxpayer_id$/i,
  
  // Email Addresses
  /^email$/i,
  /^emailaddress$/i,
  /^email_address$/i,
  /^emailaddr$/i,
  /^e_mail$/i,
  /^mail$/i,
  
  // Phone Numbers
  /^phone$/i,
  /^phonenumber$/i,
  /^phone_number$/i,
  /^mobile$/i,
  /^cell$/i,
  /^cellular$/i,
  /^telephone$/i,
  /^tel$/i,
  
  // Date of Birth
  /^dob$/i,
  /^dateofbirth$/i,
  /^date_of_birth$/i,
  /^birthdate$/i,
  /^birth_date$/i,
  /^birthday$/i,
  
  // Names
  /^fullname$/i,
  /^full_name$/i,
  /^firstname$/i,
  /^first_name$/i,
  /^lastname$/i,
  /^last_name$/i,
  /^middlename$/i,
  /^middle_name$/i,
  /^name$/i,
  
  // Addresses
  /^address$/i,
  /^street$/i,
  /^streetaddress$/i,
  /^street_address$/i,
  /^homeaddress$/i,
  /^home_address$/i,
  /^mailingaddress$/i,
  /^mailing_address$/i,
  /^billingaddress$/i,
  /^billing_address$/i,
  /^zipcode$/i,
  /^zip_code$/i,
  /^postalcode$/i,
  /^postal_code$/i,
  
  // Financial Information
  /^creditcard$/i,
  /^credit_card$/i,
  /^cardnumber$/i,
  /^card_number$/i,
  /^ccnumber$/i,
  /^cc_number$/i,
  /^cvv$/i,
  /^cvc$/i,
  /^accountnumber$/i,
  /^account_number$/i,
  /^routingnumber$/i,
  /^routing_number$/i,
  /^bankaccount$/i,
  /^bank_account$/i,
  /^iban$/i,
  /^swift$/i,
  /^bic$/i,
  
  // Government IDs
  /^nationalid$/i,
  /^national_id$/i,
  /^citizenid$/i,
  /^citizen_id$/i,
  /^visanumber$/i,
  /^visa_number$/i,
  /^greencard$/i,
  /^green_card$/i,
  
  // Medical Information
  /^medicalrecord$/i,
  /^medical_record$/i,
  /^diagnosis$/i,
  /^prescription$/i,
  /^medication$/i,
  /^healthrecord$/i,
  /^health_record$/i,
  
  // Session and Authentication
  /^sessionid$/i,
  /^session_id$/i,
  /^authtoken$/i,
  /^auth_token$/i,
  /^bearertoken$/i,
  /^bearer_token$/i
];

/**
 * Configuration options for sensitive data redaction
 */
export interface RedactionOptions {
  /** Custom sensitive field patterns to use instead of defaults */
  sensitivePatterns?: RegExp[];
  
  /** Additional patterns to add to the default patterns */
  additionalPatterns?: RegExp[];
  
  /** Replacement text for redacted values */
  redactionText?: string;
  
  /** Maximum depth for recursive processing (prevents infinite loops) */
  maxDepth?: number;
}

/**
 * Recursively redacts sensitive data from objects while preserving structure
 * Implements performance-optimized deep cloning and redaction
 */
export function redactSensitiveData(
  data: any,
  options: RedactionOptions = {}
): any {
  const {
    sensitivePatterns = DEFAULT_SENSITIVE_PATTERNS,
    additionalPatterns = [],
    redactionText = '[REDACTED]',
    maxDepth = 10
  } = options;

  // Combine default and additional patterns
  const allPatterns = sensitivePatterns.concat(additionalPatterns);

  return redactRecursive(data, allPatterns, redactionText, maxDepth, 0, new WeakSet());
}

/**
 * Internal recursive function that performs the actual redaction
 * Uses WeakSet to track visited objects and prevent infinite loops
 */
function redactRecursive(
  data: any,
  patterns: RegExp[],
  redactionText: string,
  maxDepth: number,
  currentDepth: number,
  visited: WeakSet<object>
): any {
  // Prevent infinite recursion
  if (currentDepth >= maxDepth) {
    return '[MAX_DEPTH_EXCEEDED]';
  }

  // Handle null and undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitive types
  if (typeof data !== 'object') {
    // For strings, redact any JWT tokens found within them
    if (typeof data === 'string') {
      // First check if the entire string is a JWT token
      if (isJWTToken(data)) {
        return redactionText;
      }
      // Then check if JWT tokens are embedded within the string
      let processedString = redactJWTsInString(data, redactionText);
      
      // Also check if the string contains JSON with sensitive data
      processedString = redactSensitiveDataInJsonString(processedString, patterns, redactionText, maxDepth, currentDepth);
      
      return processedString;
    }
    return data;
  }

  // Handle circular references
  if (visited.has(data)) {
    return '[CIRCULAR_REFERENCE]';
  }

  // Handle Date objects
  if (data instanceof Date) {
    return data;
  }

  // Handle RegExp objects
  if (data instanceof RegExp) {
    return data;
  }

  // Handle Error objects
  if (data instanceof Error) {
    return {
      name: data.name,
      message: data.message,
      stack: data.stack
    };
  }

  // Add to visited set
  visited.add(data);

  try {
    // Handle Arrays
    if (Array.isArray(data)) {
      return data.map(item => 
        redactRecursive(item, patterns, redactionText, maxDepth, currentDepth + 1, visited)
      );
    }

    // Handle Objects
    const result: any = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Check if the key matches any sensitive patterns
      const isSensitive = patterns.some(pattern => pattern.test(key));
      
      if (isSensitive) {
        result[key] = redactionText;
      } else {
        result[key] = redactRecursive(
          value, 
          patterns, 
          redactionText, 
          maxDepth, 
          currentDepth + 1, 
          visited
        );
      }
    }

    return result;
  } finally {
    // Remove from visited set when done processing
    visited.delete(data);
  }
}

/**
 * Utility function to check if a field name is considered sensitive
 * Useful for validation and testing
 */
export function isSensitiveField(
  fieldName: string,
  patterns: RegExp[] = DEFAULT_SENSITIVE_PATTERNS
): boolean {
  return patterns.some(pattern => pattern.test(fieldName));
}

/**
 * Utility function to get the default sensitive patterns
 * Useful for extending or customizing redaction behavior
 */
export function getDefaultSensitivePatterns(): RegExp[] {
  return [...DEFAULT_SENSITIVE_PATTERNS];
}

/**
 * Detects if a string value is likely a JWT or JWE token
 * JWT tokens have the format: header.payload.signature (3 base64url parts separated by dots)
 * JWE tokens have the format: header.key.iv.ciphertext.tag (5 base64url parts separated by dots)
 */
function isJWTToken(value: string): boolean {
  if (typeof value !== 'string' || value.length < 10) {
    return false;
  }

  // JWT tokens have exactly 3 parts, JWE tokens have exactly 5 parts
  const parts = value.split('.');
  if (parts.length !== 3 && parts.length !== 5) {
    return false;
  }

  // Each part should be base64url encoded (letters, numbers, -, _)
  const base64UrlPattern = /^[A-Za-z0-9_-]+$/;
  
  // Check if all parts look like base64url and have reasonable lengths
  return parts.every(part => 
    part.length >= 4 && base64UrlPattern.test(part)
  );
}

/**
 * Redacts JWT tokens found within strings while preserving the rest of the content
 * This handles cases where JWT tokens are embedded in JSON strings or other text
 * Supports both JWT (3 parts) and JWE (5 parts) tokens
 */
function redactJWTsInString(value: string, redactionText: string = '[REDACTED_JWT]'): string {
  if (typeof value !== 'string') {
    return value;
  }

  // Pattern to find JWT/JWE tokens within strings
  // JWT format: base64.base64.base64 (3 parts)
  // JWE format: base64.base64.base64.base64.base64 (5 parts)
  // Both start with 'eyJ' (base64 encoded '{"')
  const jwtPattern = /eyJ[A-Za-z0-9_-]{10,}(?:\.[A-Za-z0-9_-]{10,}){2,4}/g;
  
  return value.replace(jwtPattern, (match) => {
    // Count the number of parts
    const parts = match.split('.');
    // Valid JWT has 3 parts, valid JWE has 5 parts
    if (parts.length === 3 || parts.length === 5) {
      return redactionText;
    }
    return match; // If not a valid JWT/JWE, leave it unchanged
  });
}

/**
 * Enhanced redaction that also checks for JWT tokens in string values
 * and handles deeply nested authentication objects
 */
export function redactAuthenticationData(data: any): any {
  const authPatterns = [
    ...DEFAULT_SENSITIVE_PATTERNS,
    // Additional authentication-specific patterns
    /^credentials$/i,
    /^auth$/i,
    /^authentication$/i,
    /^signin$/i,
    /^login$/i,
    /^session$/i,
    /^payload$/i, // JWT payload can contain sensitive data
    
    // JWT/OAuth specific fields that should be redacted
    /^sub$/i,           // Subject identifier
    /^iss$/i,           // Issuer
    /^aud$/i,           // Audience
    /^jti$/i,           // JWT ID
    /^event_id$/i,      // Event identifier
    /^client_id$/i,     // OAuth client ID
    /^scope$/i,         // OAuth scope (can contain sensitive info)
    /^username$/i,      // Username in payload
    /^email$/i,         // Email address
    /^phone$/i,         // Phone number
    /^custom:.*$/i,     // Any custom Cognito attributes
    /^cognito:.*$/i,    // Cognito-specific attributes
    /^account.*$/i,     // Account-related fields
    
    // Additional token-like field patterns
    /^refresh.*$/i,     // Refresh token fields
    /^access.*$/i,      // Access token fields
    /^id.*token$/i,     // ID token fields
    
    // AWS Cognito specific fields
    /^userpoolid$/i,    // Cognito User Pool ID
    /^clientid$/i,      // OAuth/Cognito Client ID
    /^endpoint$/i,      // API endpoints (can reveal infrastructure)
    /^userdata$/i,      // User data objects
    /^attributes$/i,    // User attributes
    /^keyprefix$/i,     // Storage key prefixes
    /^userdatakey$/i,   // User data storage keys
    
    // Storage and session related
    /^storage$/i,       // Storage objects
    /^session$/i,       // Session objects
    /^pool$/i,          // Connection pool objects
    
    // User identification patterns
    /^userid$/i,        // User ID
    /^user_id$/i,       // User ID (underscore)
    /^useridentifier$/i, // User identifier
    /^identifier$/i,    // Generic identifier
    
    // Additional sensitive patterns
    /^mfa$/i,           // MFA related fields
    /^preferredmfa$/i,  // Preferred MFA setting
    /^authenticationflow$/i, // Auth flow type
    /^clockdrift$/i,    // Clock drift (can be sensitive)
    
    // Storage key patterns (for keys that contain sensitive data)
    /CognitoIdentityServiceProvider\./i, // Cognito storage keys
    /\.idToken$/i,      // Storage keys ending with idToken
    /\.accessToken$/i,  // Storage keys ending with accessToken
    /\.refreshToken$/i, // Storage keys ending with refreshToken
    /\.userData$/i,     // Storage keys ending with userData
    /LastAuthUser$/i,   // Last authenticated user keys
    
    // URL and endpoint patterns
    /^url$/i,           // URL fields
    /^endpoint$/i,      // Endpoint fields
    /^host$/i,          // Host fields
    /^hostname$/i,      // Hostname fields
  ];

  return redactSensitiveData(data, {
    additionalPatterns: authPatterns,
    redactionText: '[REDACTED]'
  });
}

/**
 * Redacts sensitive data within JSON strings
 * Handles cases where sensitive data is stored as stringified JSON
 */
function redactSensitiveDataInJsonString(
  value: string, 
  patterns: RegExp[], 
  redactionText: string,
  maxDepth: number,
  currentDepth: number
): string {
  if (typeof value !== 'string' || value.length < 10) {
    return value;
  }
  
  // Check if the string looks like JSON (starts with { or [)
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }
  
  try {
    // Try to parse as JSON
    const parsed = JSON.parse(value);
    
    // Recursively redact the parsed object
    const redacted = redactRecursive(parsed, patterns, redactionText, maxDepth, currentDepth + 1, new WeakSet());
    
    // Convert back to JSON string
    return JSON.stringify(redacted);
  } catch (error) {
    // If parsing fails, return the original string
    return value;
  }
}

/**
 * Utility function to redact JWT tokens from strings
 * Useful for processing strings that may contain embedded JWT tokens
 */
export function redactJWTTokensFromString(value: string, redactionText: string = '[REDACTED_JWT]'): string {
  return redactJWTsInString(value, redactionText);
}

/**
 * Enhanced value-based redaction that detects sensitive data patterns in values
 * This function checks the actual content of values, not just field names
 */
export function redactSensitiveValues(data: any, options: RedactionOptions = {}): any {
  const { redactionText = '[REDACTED]' } = options;
  
  return redactValueRecursive(data, redactionText, 0, 10, new WeakSet());
}

/**
 * Internal function for value-based redaction
 */
function redactValueRecursive(
  data: any,
  redactionText: string,
  currentDepth: number,
  maxDepth: number,
  visited: WeakSet<object>
): any {
  if (currentDepth >= maxDepth || data === null || data === undefined) {
    return data;
  }

  if (typeof data !== 'object') {
    if (typeof data === 'string') {
      return redactSensitiveStringValue(data, redactionText);
    }
    return data;
  }

  if (visited.has(data)) {
    return '[CIRCULAR_REFERENCE]';
  }

  if (data instanceof Date || data instanceof RegExp) {
    return data;
  }

  visited.add(data);

  try {
    if (Array.isArray(data)) {
      return data.map(item => 
        redactValueRecursive(item, redactionText, currentDepth + 1, maxDepth, visited)
      );
    }

    const result: any = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = redactValueRecursive(value, redactionText, currentDepth + 1, maxDepth, visited);
    }
    return result;
  } finally {
    visited.delete(data);
  }
}

/**
 * Detects and redacts sensitive patterns within string values
 */
function redactSensitiveStringValue(value: string, redactionText: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }

  let result = value;

  // SSN patterns (XXX-XX-XXXX, XXXXXXXXX)
  result = result.replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, redactionText);
  
  // Credit card patterns (groups of 4 digits)
  result = result.replace(/\b(?:\d{4}[-\s]?){3}\d{4}\b/g, redactionText);
  
  // Email patterns
  result = result.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, redactionText);
  
  // Phone number patterns (various formats)
  result = result.replace(/\b(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g, redactionText);
  
  // Driver's license patterns (varies by state, but common patterns)
  result = result.replace(/\b[A-Z]{1,2}\d{6,8}\b/g, redactionText);
  
  // Passport number patterns (typically 6-9 alphanumeric characters)
  result = result.replace(/\b[A-Z0-9]{6,9}\b/g, (match) => {
    // Only redact if it looks like a passport (mix of letters and numbers)
    if (/[A-Z]/.test(match) && /\d/.test(match)) {
      return redactionText;
    }
    return match;
  });
  
  // JWT tokens
  result = redactJWTsInString(result, redactionText);
  
  // API keys (common patterns)
  result = result.replace(/\b[A-Za-z0-9]{32,}\b/g, (match) => {
    // Only redact long alphanumeric strings that might be API keys
    if (match.length >= 32 && /[A-Za-z]/.test(match) && /\d/.test(match)) {
      return redactionText;
    }
    return match;
  });

  return result;
}

/**
 * Comprehensive redaction that combines field-name and value-based redaction
 * This is the recommended function for maximum security
 */
export function redactComprehensive(data: any, options: RedactionOptions = {}): any {
  // First pass: redact based on field names
  const fieldRedacted = redactSensitiveData(data, options);
  
  // Second pass: redact based on value patterns
  const valueRedacted = redactSensitiveValues(fieldRedacted, options);
  
  return valueRedacted;
}

/**
 * Utility function to validate redaction effectiveness
 * Returns an array of potential issues found in the data
 */
export function validateRedaction(data: any): string[] {
  const issues: string[] = [];
  
  function checkRecursive(obj: any, path: string = ''): void {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      if (typeof obj === 'string') {
        // Check for common sensitive patterns that might have been missed
        if (/\b\d{3}-?\d{2}-?\d{4}\b/.test(obj)) {
          issues.push(`Potential SSN found at ${path}`);
        }
        if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(obj)) {
          issues.push(`Potential email found at ${path}`);
        }
        if (isJWTToken(obj)) {
          issues.push(`Potential JWT token found at ${path}`);
        }
      }
      return;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        checkRecursive(item, `${path}[${index}]`);
      });
    } else {
      Object.entries(obj).forEach(([key, value]) => {
        const newPath = path ? `${path}.${key}` : key;
        
        // Check if field name suggests sensitive data but value isn't redacted
        if (isSensitiveField(key) && value !== '[REDACTED]' && typeof value === 'string') {
          issues.push(`Sensitive field "${key}" at ${newPath} may not be properly redacted`);
        }
        
        checkRecursive(value, newPath);
      });
    }
  }
  
  checkRecursive(data);
  return issues;
}